/**
 * @voltxampere/components — Buzzer dedicated component (Sprint 31-5)
 *
 * 2-pin piezo buzzer. Unlike Speaker (variable frequency), Buzzer has
 * a FIXED resonant frequency (typically 2 kHz) and an on/off behavior
 * gated by a voltage threshold (default 1 V). Sprint 31 ships the
 * schema + audio mapping; the engine stamp is scoped to Sprint 44+
 * (COMPONENT_CATALOG_v3.2 §3.17 elektromekanik group). Running a
 * circuit with Buzzer through F5 transient surfaces the canonical
 * "stamp not registered" error from `engine/stamps/register.ts` —
 * this is expected until Sprint 44 lands the proper stamp.
 *
 * Engine zero-diff invariant: this file is in `@voltxampere/components`,
 * not `@voltxampere/engine`. CORE_COMPONENT_DEFINITIONS registration
 * adds the type to the canonical registry but does NOT add a stamp;
 * the engine's `SCHEMA_SYMBOL_ALIASES` table is unchanged.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const BuzzerParamsSchema = z
    .object({
    /**
     * On/off threshold in volts. The buzzer beeps when |V_pin| >= this
     * value, silent otherwise. Default 1 V matches typical 5 V logic
     * driving a piezo (drops half across the limit resistor).
     */
    vThreshold: z.number().positive().finite().default(1),
    /**
     * Resonant frequency in Hz. Real piezo buzzers are tuned to a
     * single resonant peak; default 2 kHz is the most common educational
     * value (within human-audible mid-band, easy to hear over noise).
     */
    frequency: z.number().positive().finite().default(2000),
    /**
     * Resistance for engine MNA contribution (Sprint 44+ stamp). Sprint
     * 31 audio mapping ignores this — buzzer is treated as on/off
     * regardless of impedance.
     */
    resistance: z.number().positive().finite().default(50),
})
    .strict();
export const buzzerDefinition = {
    type: "buzzer",
    category: "passive",
    displayName: { tr: "Buzzer", en: "Buzzer" },
    symbol: "BZR",
    pins: [
        { id: "p1", side: "left", offset: 0, label: "+" },
        { id: "p2", side: "right", offset: 0, label: "-" },
    ],
    parameterSchema: BuzzerParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=buzzer.js.map